import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ActorCard from '@/components/base/ActorCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { actors } from '@/mocks/actors';

const PAGE_SIZE = 6;

export default function Actors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const totalPages = Math.max(1, Math.ceil(actors.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pageItems = useMemo(
    () => actors.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [currentPage],
  );

  const goPage = (n: number) => {
    const params = new URLSearchParams(searchParams);
    if (n <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(n));
    }
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  return (
    <SiteLayout>
      <title>演员信息列表 - 蜜桃成熟时33D相关演员资料</title>
      <meta
        name="description"
        content="演员信息页汇总蜜桃成熟时33D及相关影视作品的主要演员资料，包含演员简介与代表作品摘要，点击可查看详细个人信息。"
      />
      <meta name="keywords" content="演员信息,演员列表,蜜桃成熟时33D演员,演员资料,演员简介" />
      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员信息' }]} />

        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            演员信息
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            这里汇总了《蜜桃成熟时33D》及相关影视作品中的主要演员资料，点击任意演员卡片可查看详细个人信息与代表作品。以下内容均为虚构演示数据。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {pageItems.map((a) => (
            <ActorCard key={a.id} actor={a} movieCount={a.workIds.length} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => goPage(currentPage - 1)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line"></i>
              上一页
            </button>
            {Array.from({ length: totalPages }).map((_, idx) => {
              const n = idx + 1;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => goPage(n)}
                  className={`h-10 w-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    n === currentPage
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => goPage(currentPage + 1)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}